import * as React from 'react';

import { useQuery } from '@tanstack/react-query';

import Api from '@app/services/api';

function useDebouncedValue<T>(value: T, delayMs: number) {
  const [debounced, setDebounced] = React.useState(value);

  React.useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);

  return debounced;
}

type Options = {
  debounceMs?: number;
  minChars?: number;
  count?: number;
};

export function useDaDataAddressAutocomplete(options: Options = {}) {
  const { debounceMs = 350, minChars = 4, count = 10 } = options;

  const [query, setQuery] = React.useState('');
  const debouncedQuery = useDebouncedValue(query, debounceMs);

  const normalized = debouncedQuery.trim();
  const enabled = normalized.length >= minChars;

  const suggest = useQuery({
    queryKey: ['dadata', 'address', 'suggest', normalized, count],
    enabled,
    queryFn: ({ signal }) => Api.daData.postSuggestAddress(normalized, signal),
    staleTime: 30_000,
    cacheTime: 5 * 60_000,
  });

  const clear = React.useCallback(() => {
    setQuery('');
  }, []);

  return {
    query,
    setQuery,

    suggestions: suggest.data,
    isSuggestLoading: suggest.isFetching,
    suggestError: (suggest.error as Error) ?? null,

    clear,
  };
}
